import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { TopBar } from "@/components/layout/TopBar";
import { FOUNDER } from "@/constants/testIds";
import { altioraToast } from "@/components/altiora/toast";
import { Terminal, Shield } from "lucide-react";

export default function FounderConsole() {
  const { t, lang } = useI18n();
  const { user, refreshUser } = useAuth();
  const [flags, setFlags] = useState([]);
  const [promoting, setPromoting] = useState(false);

  const isFounder = (user?.roles || []).includes("founder") || (user?.roles || []).includes("admin");

  const load = async () => {
    const { data } = await api.get("/admin/modules");
    setFlags(data.items || []);
  };

  useEffect(() => { load(); }, []);

  const selfPromote = async () => {
    setPromoting(true);
    try {
      await api.post("/admin/founder/self-promote");
      await refreshUser();
      altioraToast.success(t("founder.promoted"));
      await load();
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Failed");
    } finally { setPromoting(false); }
  };

  const toggle = async (key, enabled) => {
    try {
      await api.put(`/admin/flags/${key}`, { enabled });
      await load();
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid={FOUNDER.root}>
      <TopBar
        title={t("founder.title")}
        subtitle={t("founder.subtitle")}
        right={
          !isFounder && (
            <button
              data-testid={FOUNDER.selfPromote}
              onClick={selfPromote} disabled={promoting}
              className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60"
            >
              <Terminal className="h-4 w-4" strokeWidth={2} />
              {promoting ? t("common.loading") : t("founder.self_promote")}
            </button>
          )
        }
      />
      <div className="px-6 md:px-10 py-8">
        {isFounder && (
          <div className="mb-6 flex items-center gap-3 rounded-md border border-[#2E8B57]/30 bg-[#2E8B57]/5 px-4 py-3">
            <Shield className="h-4 w-4 text-[#2E8B57]" strokeWidth={1.5} />
            <div className="font-mono text-xs uppercase tracking-widest text-[#2E8B57]">
              {t("founder.promoted")} · roles: {(user?.roles || []).join(", ")}
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-md border border-[#E4E1D8] bg-white">
          <div className="grid grid-cols-[1fr_120px_120px_100px] px-5 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] border-b border-[#E4E1D8]">
            <div>module</div>
            <div>status</div>
            <div>audience</div>
            <div className="text-right">enabled</div>
          </div>
          {flags.map((flag) => (
            <div
              key={flag.key}
              data-testid={FOUNDER.flagRow}
              className="grid grid-cols-[1fr_120px_120px_100px] px-5 py-4 border-b border-[#E4E1D8] last:border-b-0 items-center hover:bg-[#EBE8DE] transition-colors"
            >
              <div>
                <div className="text-sm font-medium text-[#1F2937]">{flag.meta?.[`label_${lang}`]}</div>
                <div className="mt-0.5 font-mono text-[10px] text-[#5B5B62]">{flag.key}</div>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0B2545]">{flag.status}</div>
              <div className="font-mono text-[10px] text-[#5B5B62] truncate">{(flag.audience || []).join(", ")}</div>
              <div className="text-right">
                <button
                  data-testid={FOUNDER.flagToggle}
                  disabled={!isFounder}
                  onClick={() => toggle(flag.key, !flag.enabled)}
                  className={`inline-flex items-center h-6 w-11 rounded-full border transition-colors ${
                    flag.enabled
                      ? "bg-[#0B2545] border-[#0B2545]"
                      : "bg-[#EBE8DE] border-[#E4E1D8]"
                  } ${!isFounder && "opacity-40 cursor-not-allowed"}`}
                >
                  <span className={`h-4 w-4 rounded-full bg-[#F8F7F3] shadow transform transition-transform ${
                    flag.enabled ? "translate-x-6" : "translate-x-1"
                  }`} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
